/**
 * Web Storage that cannot take a page down.
 *
 * WHY IT EXISTS. Where a browser blocks site data (Safari "Block All Cookies",
 * Chrome "Don't allow sites to save data", some sandboxed frames), merely
 * READING `window.localStorage` or `window.sessionStorage` throws a
 * SecurityError, before any getItem is called. The cart and notification
 * providers wrap every route, and they touched storage from effects with no
 * guard, so that one throw reached the root error boundary and every page on
 * the site rendered the global error screen instead of its content.
 *
 * So every access goes through here: the property read itself, and each call
 * on the object it returns, because a storage that exists can still refuse a
 * write (quota exceeded, older Safari private mode). Reads fall back to
 * "nothing stored", writes and removals are best-effort, and nothing here
 * throws. Callers carry on from memory, which is all a blocked browser offers.
 *
 * Pure apart from the storage handed in, so the node tests can drive it with a
 * plain object, the same arrangement as guest-currency.ts.
 */

/** The slice of the Web Storage API used here. `window.localStorage` satisfies it, and so does a test double. */
export interface KeyValueStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

/**
 * This browser's localStorage, or null where there is none to use: on the
 * server (no window), and where reading the property itself throws.
 */
export function browserLocalStorage(): KeyValueStorage | null {
  try {
    return typeof window === 'undefined' ? null : (window.localStorage ?? null);
  } catch {
    return null;
  }
}

/** This tab's sessionStorage, or null on the same terms as browserLocalStorage. */
export function browserSessionStorage(): KeyValueStorage | null {
  try {
    return typeof window === 'undefined' ? null : (window.sessionStorage ?? null);
  } catch {
    return null;
  }
}

/** The stored string, or null when there is none, no storage, or the storage refuses the read. */
export function readStorageItem(storage: KeyValueStorage | null | undefined, key: string): string | null {
  if (!storage) return null;
  try {
    const value = storage.getItem(key);
    return typeof value === 'string' ? value : null;
  } catch {
    return null;
  }
}

/** Store a string. Returns whether it was stored; a refusal is swallowed, never thrown. */
export function writeStorageItem(
  storage: KeyValueStorage | null | undefined,
  key: string,
  value: string,
): boolean {
  if (!storage) return false;
  try {
    storage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
}

/** Remove each key, best-effort. A refusal on one key does not stop the others. */
export function removeStorageItems(storage: KeyValueStorage | null | undefined, ...keys: string[]): void {
  if (!storage) return;
  for (const key of keys) {
    try {
      storage.removeItem(key);
    } catch { /* blocked or sealed storage: there is nothing to remove from */ }
  }
}
